import { faker } from '@faker-js/faker/locale/en'
import type { GETTruck, GETTruckWithStats } from '~/services/apiClient'

export class Truck {
  id: number
  licensePlate: string
  name: string | null
  archived: boolean
  /**
   * Green/EV status for APM's Green Lane, three-state: `true` confirmed green,
   * `false` confirmed not green, `null` never determined. Unknown is common — we only
   * learn a plate from APM's roster sync or from an appointment the legacy portal
   * reported on — so treat `null` as "no badge", never as "not green".
   */
  isGreen: boolean | null
  demoMode: boolean

  constructor(raw: GETTruck, demoMode: boolean) {
    this.id = raw.id
    if (demoMode) {
      this.licensePlate = faker.vehicle.vrm()
    } else {
      this.licensePlate = raw.license_plate_number
    }
    this.name = raw.name ?? null
    if (this.name && demoMode) {
      this.name = faker.person.firstName()
    }
    this.archived = raw.archived ?? false
    this.isGreen = raw.is_green ?? null
    this.demoMode = demoMode
  }

  toGETTruck(): GETTruck {
    return {
      id: this.id,
      license_plate_number: this.licensePlate,
      name: this.name || undefined,
      archived: this.archived,
      // Unknown stays absent: the backend COALESCEs on write, so only an explicit
      // true/false persists and an omitted value can't clear what it already knows.
      is_green: this.isGreen ?? undefined,
    }
  }
}

export class TruckWithStats {
  plate: string
  truck: Truck
  numCompleted: number | undefined
  numMissed: number | undefined
  numScheduled: number | undefined

  constructor(
    rawTruck: GETTruck,
    demoMode: boolean,
    rawStats?: GETTruckWithStats
  ) {
    this.plate = rawTruck.license_plate_number
    this.truck = new Truck(rawTruck, demoMode)
    this.numCompleted = rawStats?.num_completed
    this.numMissed = rawStats?.num_missed
    this.numScheduled = rawStats?.num_scheduled
  }

  /** Reads through to the truck — greenness belongs to the truck, not to its stats. */
  get isGreen(): boolean | null {
    return this.truck.isGreen
  }

  get missPercentage(): number | undefined {
    if (this.numCompleted === undefined || this.numMissed === undefined)
      return undefined
    return computeMissedPercentage(this.numCompleted, this.numMissed)
  }

  get numAssigned(): number | undefined {
    if (
      this.numCompleted === undefined ||
      this.numMissed === undefined ||
      this.numScheduled === undefined
    ) {
      return undefined
    }
    return this.numCompleted + this.numMissed + this.numScheduled
  }

  setStatsToZeroIfUndefined() {
    this.numCompleted = this.numCompleted ?? 0
    this.numMissed = this.numMissed ?? 0
    this.numScheduled = this.numScheduled ?? 0
  }

  toGETTruckWithStats(): GETTruckWithStats {
    return {
      truck: this.truck.toGETTruck(),
      num_completed: this.numCompleted ?? 0,
      num_missed: this.numMissed ?? 0,
      num_scheduled: this.numScheduled ?? 0,
    }
  }
}
function computeMissedPercentage(numCompleted: number, numMissed: number) {
  const total = numCompleted + numMissed
  if (total === 0) return 0
  return Math.round((numMissed / total) * 100)
}

export function truckWithStatsFromTruck(
  truck: Truck,
  stats?: GETTruckWithStats
): TruckWithStats {
  // Rebuild through `toGETTruck()` rather than a literal, so a field added to the model
  // can't be silently dropped on the way back out to the wire shape.
  return new TruckWithStats(truck.toGETTruck(), truck.demoMode, stats)
}
